import { useParams } from "react-router-dom";
import FilterComponent from "../components/FilterComponent";
import ItemCard from "../components/ItemCard";
import MenuPanel from "../components/MenuPanel";
import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useMemo, useState } from "react";
import { getProducts } from "../Redux/CounterSlice";
import {
  fetchUserAccount,
  getPropertiesReducer,
  walletBalanceFetch,
} from "../Redux/properties/PropertiesSlice";

import {
  CURRENT_PURPOSE,
  WEBSITE_CONSTANTS,
  WEBSITE_FARM_TRADE,
  WEBSITE_REAL_ESTATE,
} from "../configs/website-config";
import SalePanel from "../components/SalePanel";
import { LoginModal } from "../components/LoginModal";
import Footer from "../components/Footer";
import SecondFooter from "../components/SecondFooter";
import { FormattedMessage } from 'react-intl';

// Import required Mantine components
import { 
  Box, 
  Container, 
  Title, 
  Text, 
  Group, 
  SimpleGrid, 
  useMantineTheme,
  Select,
  Divider,
  TextInput,
  NumberInput,
  RangeSlider,
  MultiSelect,
  Checkbox,
  Stack
} from '@mantine/core';

const Marketplace = () => {
  const theme = useMantineTheme();
  let { level } = useParams();
  const [filter, setFilter] = useState({ 
    category: '', 
    product: '', 
    UPC: '', 
    product_name_id: '',
    location: '',
    physicalState: '',
    contractState: '',
    minPrice: '',
    maxPrice: '',
    period: '',
    productName: ''
  });
  
  const products = useSelector((state) => state.properties.data);

  // Enhanced filter to match item characteristics - ONLY DRAFT CONTRACTS
  const productsFilter = useMemo(() => {
    return products.filter(e => {
      // MAIN FILTER: Only show Draft contracts
      const draftFilter = e?.contractState === 'Draft';
      
      // Basic filters from original code
      const baseFilter = filter.category
        ? e?.product_name_id === filter.UPC
        : true;
        
      // New filters based on item characteristics
      const locationFilter = filter.location 
        ? e?.location?.toLowerCase().includes(filter.location.toLowerCase())
        : true;
        
      const physicalStateFilter = filter.physicalState
        ? e?.physicalState === filter.physicalState
        : true;
        
      const contractStateFilter = filter.contractState
        ? e?.contractState === filter.contractState
        : true;
        
      const productNameFilter = filter.productName
        ? e?.productName?.toLowerCase().includes(filter.productName.toLowerCase())
        : true;
        
      const periodFilter = filter.period
        ? e?.period?.includes(filter.period)
        : true;
        
      // Price filter logic would depend on how your price is formatted
      // This is a simple example that assumes price is in format "X.XX $/unit"
      const priceFilter = true;
      if (filter.minPrice || filter.maxPrice) {
        const rawPrice = e?.price?.split(' ')[0]; // Extract numeric part
        if (rawPrice) {
          const numericPrice = parseFloat(rawPrice);
          if (!isNaN(numericPrice)) {
            if (filter.minPrice && numericPrice < parseFloat(filter.minPrice)) return false;
            if (filter.maxPrice && numericPrice > parseFloat(filter.maxPrice)) return false;
          }
        }
      }
        
      return draftFilter && baseFilter && locationFilter && physicalStateFilter && 
             contractStateFilter && productNameFilter && periodFilter && priceFilter;
    });
  }, [products, filter]);

  const [salePanelModal, setSalePanelModal] = useState(false);
  const [saleSelected, setSaleSelected] = useState();

  useEffect(() => {
    if (saleSelected) console.log("saleSelected", saleSelected);
    setSalePanelModal(true);
  }, [saleSelected]);

  // Extract unique values for filters
  const uniqueLocations = useMemo(() => {
    const locations = products
      .filter(p => p.location)
      .map(p => ({ value: p.location, label: p.location }));
    return [...new Map(locations.map(item => [item.value, item])).values()];
  }, [products]);
  
  const uniquePhysicalStates = useMemo(() => {
    const states = products
      .filter(p => p.physicalState)
      .map(p => ({ value: p.physicalState, label: p.physicalState }));
    return [...new Map(states.map(item => [item.value, item])).values()];
  }, [products]);
  
  const uniqueContractStates = useMemo(() => {
    const states = products
      .filter(p => p.contractState)
      .map(p => ({ value: p.contractState, label: p.contractState }));
    return [...new Map(states.map(item => [item.value, item])).values()];
  }, [products]);
  
  const uniqueProductNames = useMemo(() => {
    const names = products
      .filter(p => p.productName)
      .map(p => ({ value: p.productName, label: p.productName }));
    return [...new Map(names.map(item => [item.value, item])).values()];
  }, [products]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilter(prev => ({ ...prev, [field]: value }));
  };

  const styles = {
      container: {
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        padding: 0,
        margin: 0,
      },
      header: {
        width: '100%',
        padding: '0px 0',
      },
      quoteSection: {
        textAlign: 'center',
        padding: '40px 0 20px', // Increased padding
        borderBottom: '1px solid #e9ecef', // Subtle border at bottom
        marginBottom: '40px', // Add space after the section
        background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)', // Subtle gradient background
      },
      quoteText: {
        fontSize: '2.2rem', // Larger font size
        fontWeight: 700, // Bolder
        color: '#2C3E50', // Darker, richer color
        margin: 0,
        fontFamily: "'Montserrat', sans-serif", // More elegant font
        letterSpacing: '-0.5px', // Tighter letter spacing
      },
      mainContent: {
        display: 'flex',
        flexDirection: 'row',
        gap: '30px', // Increased gap between sidebar and content
        padding: '0 20px 40px', // Added bottom padding
      },
      filterSidebar: {
        width: '280px',
        flexShrink: 0,
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)', // Softer shadow
        padding: '24px', // Increased padding
        alignSelf: 'flex-start',
        position: 'sticky',
        top: '20px',
        border: '1px solid #e9ecef', // Subtle border
      },
      productsSection: {
        flex: 1,
        padding: '0 10px',
      },
      footerSection: {
        marginTop: '60px', // Increased margin
        borderTop: '1px solid #e9ecef', // Add border
        paddingTop: '20px', // Add padding
      },
      sectionDivider: {
        height: '4px',
        width: '100px',
        margin: '1.5rem auto 3rem',
        background: 'linear-gradient(90deg, #3498db, #2980b9)',
        borderRadius: '2px',
      }
    };

  return (
    <>
      {saleSelected && (
        <SalePanel
          modal={salePanelModal}
          toggle={() => {
            setSalePanelModal(false);
            setSaleSelected(undefined);
          }}
          title={saleSelected?.title}
          value={saleSelected}
        />
      )}
      <Box sx={styles.container}>
        {/* Menu Panel Section */}
        {/* <Box sx={styles.header}>
          <MenuPanel level={level} />
        </Box> */}
        
        {/* Quote Section */}
        <Box sx={styles.quoteSection}>
          <Title order={2} sx={styles.quoteText}>
            {WEBSITE_CONSTANTS.QUOTE1}
          </Title>
          {/* Decorative bar beneath the title */}
          <Box  sx={styles.sectionDivider}/>
        </Box>
        
        {/* Main Content with Sidebar Layout */}
        <Container size="xl">
          <Box sx={styles.mainContent}>
            {/* Vertical Filter Sidebar */}
            <Box sx={styles.filterSidebar}>
              <Text weight={600} size="lg" mb="md">
                <FormattedMessage id="marketplace.filters.title" defaultMessage="Filters" />
              </Text>
              
              {/* Product Filters */}
              <Box mb="xl">
                <Divider my="sm" />
                
                {/* Product Name */}
                <Box mb="md">
                  <Text size="sm" mb="xs">
                    <FormattedMessage id="marketplace.filters.productName" defaultMessage="Product Name" />
                  </Text>
                  <Select
                    placeholder="Select product"
                    data={uniqueProductNames}
                    clearable
                    searchable
                    value={filter.productName}
                    onChange={(value) => handleFilterChange('productName', value)}
                    maxDropdownHeight={120}
                    nothingFound="No options"
                    styles={{
                    dropdown: {
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '150px',
                      overflowY: 'auto',
                      zIndex: 10
                    }
                  }}
                  />
                </Box>
                
                {/* Physical State */}
                <Box mb="md">
                  <Text size="sm" mb="xs">
                    <FormattedMessage id="marketplace.filters.physicalState" defaultMessage="Physical State" />
                  </Text>
                  <Select
                    placeholder="Select state"
                    data={uniquePhysicalStates}
                    clearable
                    value={filter.physicalState}
                    onChange={(value) => handleFilterChange('physicalState', value)}
                    maxDropdownHeight={120}
                    nothingFound="No options"
                    styles={{
                    dropdown: {
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '150px',
                      overflowY: 'auto',
                      zIndex: 10
                    }
                  }}
                  />
                </Box>
                
                {/* Price Range */}
                <Box mb="md">
                  <Text size="sm" mb="xs">
                    <FormattedMessage id="marketplace.filters.priceRange" defaultMessage="Price Range" />
                  </Text>
                  <Group grow>
                    <NumberInput
                      placeholder="Min"
                      min={0}
                      step={0.1}
                      precision={2}
                      value={filter.minPrice}
                      onChange={(value) => handleFilterChange('minPrice', value)}
                    />
                    <NumberInput
                      placeholder="Max"
                      min={0}
                      step={0.1}
                      precision={2}
                      value={filter.maxPrice}
                      onChange={(value) => handleFilterChange('maxPrice', value)}
                    />
                  </Group>
                </Box>
              </Box>
              
              {/* Contract Filters */}
              <Box mb="xl">
                <Text weight={600} size="md" mb="md">
                  <FormattedMessage id="marketplace.filters.contract" defaultMessage="Contract" />
                </Text>
                
                <Divider my="sm" />
                
                {/* Contract State */}
                <Box mb="md">
                  <Text size="sm" mb="xs">
                    <FormattedMessage id="marketplace.filters.contractState" defaultMessage="Contract State" />
                  </Text>
                  <Select
                    placeholder="Select state"
                    data={uniqueContractStates}
                    clearable
                    value={filter.contractState}
                    onChange={(value) => handleFilterChange('contractState', value)}
                    maxDropdownHeight={120}
                    nothingFound="No options"
                    styles={{
                    dropdown: {
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '150px',
                      overflowY: 'auto',
                      zIndex: 10
                    }
                  }}
                  />
                </Box>
                
                {/* Period */}
                <Box mb="md">
                  <Text size="sm" mb="xs">
                    <FormattedMessage id="marketplace.filters.period" defaultMessage="Period" />
                  </Text>
                  <TextInput
                    placeholder="Enter date range"
                    value={filter.period}
                    onChange={(e) => handleFilterChange('period', e.target.value)}
                  />
                </Box>
              </Box>
              
              {/* Location Filters */}
              <Box mb="xl">
                <Text weight={600} size="md" mb="md">
                  <FormattedMessage id="marketplace.filters.location" defaultMessage="Location" />
                </Text>
                
                <Divider my="sm" />
                
                {/* Location Search */}
                <Box mb="md">
                  <Text size="sm" mb="xs">
                    <FormattedMessage id="marketplace.filters.searchLocation" defaultMessage="Search Location" />
                  </Text>
                  <Select
                    placeholder="Select location"
                    data={uniqueLocations}
                    clearable
                    searchable
                    value={filter.location}
                    onChange={(value) => handleFilterChange('location', value)}
                    maxDropdownHeight={120}
                    nothingFound="No options"
                    styles={{
                    dropdown: {
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '150px',
                      overflowY: 'auto',
                      zIndex: 10
                    }
                  }}
                  />
                </Box>
              </Box>
              
              {/* Original Filter Component (if needed) */}
              <FilterComponent setFilter={setFilter} filter={filter} />
            </Box>
            
            {/* Products Section */}
            <Box sx={styles.productsSection}>
              <Items selectedSale={setSaleSelected} products={productsFilter} />
            </Box>
          </Box>
        </Container>
        
        {/* Footer Section */}
        {/* <Box sx={styles.footerSection}>
          <SecondFooter />
          <Footer />
        </Box> */}
      </Box>
      
      <LoginModal />
    </>
  );
};

// Updated Items component using CommodityProductCard
const Items = ({ selectedSale, products }) => {
  const data12 = useSelector((state) => state.counter.data);
  const dispatch = useDispatch();
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      loadDetails();
    }
  }, [fetched]);

  const loadDetails = async () => {
    if (CURRENT_PURPOSE === WEBSITE_REAL_ESTATE) {
      dispatch(getPropertiesReducer());
    } else {
      dispatch(getProducts());
    }
  };

  // Import the new CommodityProductCard
  const CommodityProductCard = React.lazy(() => import('../components/CommodityProductCard'));

  return (
    <Box>
      <Group position="apart" mb="md">
        <Text weight={600} size="lg">
          {CURRENT_PURPOSE === WEBSITE_REAL_ESTATE ? (
            <FormattedMessage id="marketplace.products.propertiesTitle" defaultMessage="Properties" />
          ) : (
            <FormattedMessage id="marketplace.products.title" defaultMessage="Products" />
          )}
        </Text>
        <Group spacing="md">
          <Text size="sm" color="dimmed">
            <FormattedMessage 
              id="marketplace.products.itemsFound" 
              defaultMessage="{count} items found" 
              values={{ count: products.length }} 
            />
          </Text>
          
          {/* Sort dropdown */}
          <Select
            size="sm"
            style={{ width: 200 }}
            placeholder={<FormattedMessage id="marketplace.sort.label" defaultMessage="Sort by" />}
            data={[
              { value: 'relevance', label: 'Relevance' },
              { value: 'price_asc', label: 'Price: Low to High' },
              { value: 'price_desc', label: 'Price: High to Low' },
              { value: 'newest', label: 'Newest First' },
            ]}
            defaultValue="relevance"
            maxDropdownHeight={120}
            styles={{
                    dropdown: {
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '150px',
                      overflowY: 'auto',
                      zIndex: 10
                    }
                  }}
          />
        </Group>
      </Group>
      
      {/* Products grid with conditional rendering of the appropriate card component */}
      <SimpleGrid
        cols={3}
        spacing="xl"
        breakpoints={[
          { maxWidth: 1200, cols: 2, spacing: "lg" },
          { maxWidth: 600, cols: 1, spacing: "md" },
        ]}
      >
        {CURRENT_PURPOSE === WEBSITE_FARM_TRADE &&
          data12.map((x, ind) => (
            <Box key={ind} sx={{ padding: '10px' }}>
              <React.Suspense fallback={<div>Loading...</div>}>
                <CommodityProductCard selectedSale={selectedSale} item={x} />
              </React.Suspense>
            </Box>
          ))}
          
        {CURRENT_PURPOSE === WEBSITE_REAL_ESTATE &&
          products.map((x, ind) => {
            if (x.publishId === 1) {
              return (
                <Box key={ind} sx={{ padding: '10px' }}>
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CommodityProductCard selectedSale={selectedSale} item={x} />
                  </React.Suspense>
                </Box>
              );
            }
            return null;
          })}
      </SimpleGrid>
    </Box>
  );
};

export default Marketplace;